import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CenterContent } from "shared/styles";
import { APIErrorMessage, Title } from "shared/components";
import { copy } from "shared/assets";
import { Spacer } from "shared/layout";
import { ContentWidth } from "shared/styles/constants";
import { SubscriptionList } from "Bands/pages/subscription/containers";
import {
  useBandSubscriptionContext,
  useCompanyContext,
  useSubscriptionContext,
} from "Bands/contexts";
import { BAND_ACTIONS, SubscriptionType } from "Bands/types";
import { clearActionMeta, dataMetaSelector } from "Bands/state";
import { IAPIState, IJSONAPIErrorCodes, IStatus } from "shared/types";
import {
  useClearActionMetaOnUnMount,
  useCompanyRoute,
  useCreateBandSubscription,
  useSubscriptionError,
} from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import { addTemporaryToast } from "shared/components/Toast";
import { SUBSCRIPTIONS } from "Bands/constants";

const { page: subscriptionCopy, editPlan: editPlanCopy } = copy.subscription;

export const EditSubscriptionPlan = () => {
  const dispatch = useDispatch();

  const {
    editSubscription,
    selectedSubscription,
    chooseSubscription,
    chooseBillingMethod,
    handleSubscribe,
  } = useSubscriptionContext();
  const { currentSubscription, fetchBandSubscriptions } =
    useBandSubscriptionContext();
  const { userCompanies } = useCompanyContext();

  const { pushRoute } = useCompanyRoute();

  const { api: editApiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editBandSubscription)
  );
  useClearActionMetaOnUnMount(BAND_ACTIONS.editBandSubscription);

  const { apiMeta: createApiMeta, customSubscriptionApiMeta } =
    useCreateBandSubscription();

  useEffect(() => {
    if (editApiMeta?.status === IAPIState.succeeded) {
      fetchBandSubscriptions();

      const companyId = currentSubscription?.company?.id?.split("/")?.[3];

      if (companyId) {
        pushRoute(BAND_PATHS.COMPANY_DASHBOARD, { companyId });

        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: editPlanCopy.success,
          })
        );
      }
    }
  }, [
    dispatch,
    editApiMeta,
    pushRoute,
    currentSubscription,
    fetchBandSubscriptions,
  ]);

  const isMissingPaymentError = useMemo(() => {
    const { errorCodes } = editApiMeta?.error || {};

    return (
      errorCodes?.includes(IJSONAPIErrorCodes.MissingPaymentProvider) ||
      errorCodes?.includes(
        IJSONAPIErrorCodes.MissingPaymentProviderSubscriptionId
      )
    );
  }, [editApiMeta?.error]);

  useEffect(() => {
    // edit susbcription returns missing payment provider error
    // create a new subscription
    if (isMissingPaymentError) {
      handleSubscribe();
    }
  }, [isMissingPaymentError, handleSubscribe]);

  const isLoading = useMemo(() => {
    if (
      (selectedSubscription && editApiMeta?.status === IAPIState.pending) ||
      customSubscriptionApiMeta?.status === IAPIState.pending
    ) {
      return selectedSubscription
        ? { [selectedSubscription.plan]: true }
        : undefined;
    }
  }, [selectedSubscription, editApiMeta, customSubscriptionApiMeta]);

  const hasFailed = useMemo(() => {
    if (isMissingPaymentError) return false;

    if (
      selectedSubscription &&
      (editApiMeta?.status === IAPIState.failed ||
        createApiMeta?.status === IAPIState.failed ||
        customSubscriptionApiMeta?.status === IAPIState.failed)
    ) {
      return true;
    }

    return false;
  }, [
    isMissingPaymentError,
    selectedSubscription,
    editApiMeta,
    createApiMeta,
    customSubscriptionApiMeta,
  ]);

  const { error } = useSubscriptionError(
    editApiMeta?.error ||
      createApiMeta?.error ||
      customSubscriptionApiMeta?.error
  );

  const onSelect = (plan: SubscriptionType) => {
    if (!currentSubscription?.company?.id) return;

    dispatch(clearActionMeta(BAND_ACTIONS.editBandSubscription));
    dispatch(clearActionMeta(BAND_ACTIONS.createBandSubscription));
    dispatch(clearActionMeta(BAND_ACTIONS.requestCustomSubscription));

    const companyId = currentSubscription?.company?.id?.split("/")?.[3];

    chooseSubscription(plan);
    chooseBillingMethod(companyId);

    if (plan === SubscriptionType.custom) {
      const company = userCompanies.find(({ id }) => id === companyId);

      handleSubscribe(company, SUBSCRIPTIONS[plan]);
      return;
    }

    editSubscription({ plan, currentSubscription });
  };

  if (!currentSubscription) return null;

  return (
    <CenterContent width={ContentWidth.wide}>
      <Spacer size="xxl" align="center">
        <Title asComponent="h2" title={subscriptionCopy.change} />
        <SubscriptionList onSelect={onSelect} isLoading={isLoading} />
        {hasFailed && (
          <APIErrorMessage
            apiErrorMessage={error}
            apiError={
              editApiMeta?.error ||
              createApiMeta?.error ||
              customSubscriptionApiMeta?.error
            }
          />
        )}
      </Spacer>
    </CenterContent>
  );
};
